import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { OrderDeclineType } from 'types/enums/OrderDeclineType';
import { ChatSticker } from 'types/interfaces/chat/ChatSticker';
import { ChatStickerPack } from 'types/interfaces/chat/ChatStickerPack';

import { StickersApi } from 'api/StickersApi';
import { updateRecentAssets } from 'helpers/chat-assets';
import { useStickersAnalytics } from 'hooks/analytics/useStickersAnalytics';
import { useMicroPayment } from 'hooks/useMicroPayment';
import { useToast } from 'hooks/useToast';
import { sendStickerMessage } from 'store/messenger/thunks';
import {
  getOneClickPaymentDeclineType,
  getOneClickPaymentOrderId,
} from 'store/oneClickPayment/selectors';

import { Spinner, SpinnerTypes } from 'components/base/Spinner';
import { Payment3dsPopup } from 'components/shared/Payment3dsPopup';
import { Popup } from 'components/UI/Popup';

import { BuyStickerPopupContent } from './submodules/BuyStickerPopupContent';
import { BuyStickerPopupSuccess } from './submodules/BuyStickerPopupSuccess';

import css from './buyStickerPopup.module.sass';

type ActionsPayload =
  | BasePopupActionPayload<'submit'>
  | BasePopupActionPayload<'close'>;

export interface BuyStickerPopupProps {
  threadId: number;
  stickerPackId: number;
  onSuccessPayment: () => void;
  hideStickerInput: () => void;
  stickerId?: number;
}

type Props = BasePopupProps<
  BuyStickerPopupProps,
  (action: ActionsPayload) => void
>;

export const BuyStickerPopup: React.FC<Props> = ({
  data: {
    threadId,
    stickerPackId,
    stickerId,
    onSuccessPayment,
    hideStickerInput,
  },
  onAction,
}) => {
  const dispatch = useDispatch();

  const { showErrorToast } = useToast();

  const [stickerPack, setStickerPack] = useState<ChatStickerPack | null>(null);
  const [stickerPackStickers, setStickerPackStickers] = useState<
    ChatSticker[] | null
  >(null);

  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  const declineType = useSelector(getOneClickPaymentDeclineType);
  const orderId = useSelector(getOneClickPaymentOrderId);

  const { trackShowStickerPopup } = useStickersAnalytics();

  const currentSticker = useMemo(() => {
    if (!stickerPackStickers) return null;

    return (
      stickerPackStickers.find((stickerItem) => stickerItem.id === stickerId) ||
      stickerPackStickers?.[0]
    );
  }, [stickerId, stickerPackStickers]);

  const successStickersPackImages = useMemo(() => {
    if (!stickerPackStickers) return [];

    return stickerPackStickers
      .slice(0, 3)
      .map((stickerItem) => stickerItem.big_url);
  }, [stickerPackStickers]);

  const isEnabledCvvField = useMemo(
    () => declineType === OrderDeclineType.Retryable,
    [declineType]
  );

  const handleClose = useCallback(() => {
    if (isPaymentProcessing) return;

    if (isPaymentSuccess) {
      hideStickerInput();
    }

    onAction({ type: 'close' });
  }, [hideStickerInput, isPaymentProcessing, isPaymentSuccess, onAction]);

  const handleSuccessPayment = useCallback(async () => {
    try {
      if (currentSticker) {
        const randomId = Math.round(Math.random() * 100000);

        await dispatch(
          sendStickerMessage({
            messageId: randomId,
            contactId: threadId,
            sticker: currentSticker,
          })
        );

        updateRecentAssets(currentSticker);

        onSuccessPayment();
      }
    } finally {
      setIsPaymentSuccess(true);
      setIsPaymentProcessing(false);

      trackShowStickerPopup({
        info: 'sticker_buy_pack_success',
        order_id: orderId,
      });
    }
  }, [
    onSuccessPayment,
    currentSticker,
    dispatch,
    threadId,
    trackShowStickerPopup,
    orderId,
  ]);

  const handlePopupClose = useCallback(() => {
    onAction({ type: 'close' });
    trackShowStickerPopup({
      info: 'sticker_buy_pack_decline',
      order_id: orderId,
    });
  }, [onAction, orderId, trackShowStickerPopup]);

  const { verifyUrl, handlePayment } = useMicroPayment({
    pack: stickerPack?.package_info,
    onSuccess: handleSuccessPayment,
    onFail: handlePopupClose,
  });

  const handleAccept = useCallback(
    (newValues: Record<string, any>) => {
      setIsPaymentProcessing(true);
      handlePayment({ cvv: newValues.cvv }, handlePopupClose);
    },
    [handlePayment, handlePopupClose]
  );

  const handleStickerView = useCallback(() => {
    onAction({ type: 'close' });
    setIsPaymentSuccess(false);
  }, [onAction]);

  const getPopupData = useCallback(async () => {
    try {
      const stickerPackData = await StickersApi.fetchStickerPack({
        packId: stickerPackId,
      });

      setStickerPack(stickerPackData);
    } catch (err) {
      showErrorToast({
        title: 'Error during fetching sticker pack',
        subtitle: 'Please, try again later',
      });
      onAction({ type: 'close' });
    }

    try {
      const stickersData = await StickersApi.fetchStickers({
        packId: stickerPackId,
      });

      setStickerPackStickers(stickersData);
    } catch (error) {
      showErrorToast({
        title: 'Error during fetching preview stickers',
        subtitle: 'Please, try again later',
      });
      onAction({ type: 'close' });
    }
  }, [onAction, showErrorToast, stickerPackId]);

  useEffect(() => {
    getPopupData();
  }, [getPopupData]);

  const renderPopupContent = useCallback(() => {
    if (!stickerPackStickers || !stickerPack?.package_info)
      return <Spinner type={SpinnerTypes.Dark} key="spinner" />;

    if (isPaymentSuccess)
      return (
        <BuyStickerPopupSuccess
          key="success-popup"
          defaultPackPreviewUrl={
            currentSticker?.big_url || currentSticker?.preview_url
          }
          defaultStickerPreviewUrl={
            stickerPack?.big_url || stickerPack?.preview_url
          }
          stickersPacksImages={successStickersPackImages}
          onSubmit={handleStickerView}
        />
      );

    return (
      <BuyStickerPopupContent
        key="payment-popup"
        isLoading={isPaymentProcessing}
        isEnabledCvvField={isEnabledCvvField}
        stickerPackage={stickerPack.package_info}
        defaultPackPreviewUrl={
          currentSticker?.big_url || currentSticker?.preview_url
        }
        defaultStickerPreviewUrl={
          stickerPack?.big_url || stickerPack?.preview_url
        }
        onSubmit={handleAccept}
      />
    );
  }, [
    currentSticker?.big_url,
    currentSticker?.preview_url,
    handleAccept,
    handleStickerView,
    isEnabledCvvField,
    isPaymentProcessing,
    isPaymentSuccess,
    stickerPack?.big_url,
    stickerPack?.package_info,
    stickerPack?.preview_url,
    stickerPackStickers,
    successStickersPackImages,
  ]);

  return (
    <>
      <Popup zIndex={99} onClose={handleClose}>
        <div className={css.root}>{renderPopupContent()}</div>
      </Popup>

      {!!verifyUrl && <Payment3dsPopup url={verifyUrl} />}
    </>
  );
};
